import {defineAsyncComponent} from "vue";
import {AbstractInteractionMode, DrawStyleUtil} from "./abstract-interaction-mode";
import {Layer, LayerGeometry} from "../../document/model/layer";
import store from "../../../js/store";
import {MapEvent} from "../../document/model/map";
import {Feature} from "geojson";
import {EditorSimpleSelect} from "../interaction/editor/editor-simple-select";
import {EditorDeleteSelect} from "../interaction/editor/editor-delete-select";
import {EditorDrawPolygon} from "../interaction/editor/editor-draw-polygon";
import {EditorDrawCircle} from "../interaction/editor/editor-draw-circle";
import {EditorDrawLinestring} from "../interaction/editor/editor-draw-linestring";
import {EditorFeatureTransformTranslate} from "../interaction/editor/editor-feature-transform-translate";
import {EditorFeatureTransformScale} from "../interaction/editor/editor-feature-transform-scale";
import {EditorFeatureTransformRotate} from "../interaction/editor/editor-feature-transform-rotate";
import {EditorVertexTransformTranslate} from "../interaction/editor/editor-vertex-transform-translate";
import {EditorVertexDraw} from "../interaction/editor/editor-vertex-draw";
import {EditorVertexDelete} from "../interaction/editor/editor-vertex-delete";
import {EditorClone} from "../interaction/editor/editor-clone";
import {EditorSplitPolygon} from "../interaction/editor/editor-split-polygon";
import {EditorDrawPoint} from "../interaction/editor/editor-draw-point";
import {EditorPointTransformTranslate} from "../interaction/editor/editor-point-transform-translate";
import {EditorDrawParallelogram} from "../interaction/editor/editor-draw-parallelogram";
import {EditorSplitLinestring} from "../interaction/editor/editor-split-linestring";
import {EditorBuffer} from "../interaction/editor/editor-buffer";
import {EditorOffsetLine} from "../interaction/editor/editor-offset-line";
import {isOffline} from "../../utils";
import EventBus from '@/js/event-bus';
import {GeometryUtil} from "../geometry";
import {features} from "process";

let MapMultiSave = defineAsyncComponent(() => import("../../../components/map/MapMultiSave.vue"));

export class EditorMode extends AbstractInteractionMode {
  static identifier: string = "editor";
  static title: string = "Editor simplu";
  static icon: string = "/icons/toolbox/map-edit.svg";
  static requiresLayer: boolean = true;
  static allowChanges: boolean = true;

  static drawStyles: any = DrawStyleUtil.drawStyle({
    "line-color": '#D20C0C',
    "line-dasharray": [0.2, 2],
    "fill-color": '#D20C0C',
    "fill-opacity": 0.3,
    "border-color": '#D20C0C',
    "line-width": 5
  });

  static interactions = {
    'select': EditorSimpleSelect.definition({selectionAllowed: true, multiSelection: true}),
    'editor_select': EditorSimpleSelect.definition({selectionAllowed: true, multiSelection: true}),
    'delete_select': EditorDeleteSelect.definition({selectionAllowed: true, multiSelection: true}),
    'draw_polygon': EditorDrawPolygon.definition({selectionAllowed: false, changeset: 'draw_polygon'}),
    'draw_linestring': EditorDrawLinestring.definition({changeset: 'draw_linestring'}),
    'draw_point': EditorDrawPoint.definition({changeset: 'draw_point'}),
    'draw_parallelogram': EditorDrawParallelogram.definition({
      selectionAllowed: false,
      changeset: 'draw_paralellogram'
    }),
    'point_transform_translate': EditorPointTransformTranslate.definition({
      selectionAllowed: true,
      selectionRequired: true,
      changeset: 'transform',
    }),
    'feature_transform_translate': EditorFeatureTransformTranslate.definition({
      selectionAllowed: true,
      selectionRequired: true,
      changeset: 'transform',
    }),
    'feature_transform_scale': EditorFeatureTransformScale.definition({
      selectionAllowed: true,
      selectionRequired: true,
      changeset: 'transform',
    }),
    'feature_transform_rotate': EditorFeatureTransformRotate.definition({
      selectionAllowed: true,
      selectionRequired: true,
      changeset: 'transform',
    }),
    'vertex_transform_translate': EditorVertexTransformTranslate.definition({
      selectionAllowed: true,
      selectionRequired: true,
      changeset: 'transform',
    }),
    'vertex_draw': EditorVertexDraw.definition({
      selectionAllowed: true,
      selectionRequired: true,
      changeset: 'transform',
    }),
    'vertex_delete': EditorVertexDelete.definition({
      selectionAllowed: true,
      selectionRequired: true,
      changeset: 'transform',
    }),
    'clone': EditorClone.definition({
      selectionAllowed: true,
      selectionRequired: true,
      changeset: 'transform'
    }),
    'split_polygon': EditorSplitPolygon.definition({
      selectionAllowed: false,
      selectionRequired: true,
      changeset: 'split_polygon'
    }),
    'split_linestring': EditorSplitLinestring.definition({
      selectionAllowed: false,
      selectionRequired: true,
      changeset: 'split_linestring',
    }),
    'buffer': EditorBuffer.definition({
      selectionAllowed: false,
      selectionRequired: true,
      changeset: 'buffer',
    }),
    'offset_line': EditorOffsetLine.definition({
      selectionAllowed: false,
      selectionRequired: true,
      changeset: 'offset_line'
    }),
    'draw_circle': EditorDrawCircle.definition({selectionAllowed: false, changeset: 'draw_polygon'}),
  }

  getGlobalActions(drawMode) {
    let options = [];

    options.push({
      label: "Select",
      icon: "/icons/toolbox/editor-select.svg",
      active: drawMode === "select",
      command: () => this.changeInteraction("select"),
      pos: {position: 'bottom'}
    });

    options.push({
      label: "Edit attributes",
      icon: "/icons/toolbox/table-edit-icon.svg",
      active: drawMode === "editor_select",
      command: () => this.changeInteraction("editor_select"),
      pos: {position: 'bottom'}
    });

    switch (this.layer.properties?.shapeType?.title) {
      case "MultiPolygon":
        options.push(
          {
            label: "Add new polygon",
            icon: "/icons/toolbox/polygon-draw.svg",
            command: () => this.changeInteraction("draw_polygon"),
            active: drawMode === "draw_polygon",
            pos: {position: 'bottom'}
          }
        );
        options.push(
          {
            label: "Add circle",
            icon: "/icons/toolbox/circle-o.svg",
            command: () => this.changeInteraction("draw_circle"),
            active: drawMode === "draw_circle",
            pos: {position: 'bottom'}
          }
        );
        options.push(
          {
            label: "Add parallelogram grid",
            icon: "/icons/toolbox/grid.svg",
            command: () => this.changeInteraction("draw_parallelogram"),
            active: drawMode === "draw_parallelogram",
            pos: {position: 'bottom'}
          }
        );
        break;
      case "MultiLine":
        options.push(
          {
            label: "Add new line",
            icon: "/icons/toolbox/polyline-pt.svg",
            command: () => this.changeInteraction("draw_linestring"),
            active: drawMode === "draw_linestring",
            pos: {position: 'bottom'}
          }
        );
        break;
      case "MultiPoint":
        options.push(
          {
            label: "Add new point",
            icon: "/icons/toolbox/point.svg",
            command: () => this.changeInteraction("draw_point"),
            active: drawMode === "draw_point",
            pos: {position: 'bottom'}
          }
        );
        break;
      default:
        this.controller.configuration.toastProvider({
          severity: "error",
          summary: "Error",
          detail: this.controller.configuration.translatorProvider(`Unknown shapeType!`),
          life: 5000,
        });
      //throw new Error(`Unknown shapeType ${this.layer.properties?.shapeType?.title}`);
    }

    options.push({
      label: "Delete",
      icon: "/icons/toolbox/delete.svg",
      command: () => this.changeInteraction("delete_select"),
      active: drawMode === "delete_select",
      pos: {position: 'bottom'}
    })

    return options;
  }

  getSingleActions(layerGeometry, interactionIdentifier, point: mapboxgl.Point | undefined = undefined) {
    let options = [];
    if (layerGeometry.geometry.geometry.type !== 'Point') {
      options.push({
        label: 'Translate transform',
        icon: '/icons/toolbox/move.svg',
        command: () => this.changeInteraction('feature_transform_translate'),
        active: interactionIdentifier === 'feature_transform_translate',
        pos: { position: 'bottom' },
      });
      options.push({
        label: 'Scale transform',
        icon: '/icons/toolbox/vector-scale.svg',
        command: () => this.changeInteraction('feature_transform_scale'),
        active: interactionIdentifier === 'feature_transform_scale',
        pos: { position: 'bottom' },
      });

      if (layerGeometry.geometry.customProperties?.meta_type != 'circle') {
        options.push({
          label: 'Rotate transform',
          icon: '/icons/toolbox/vector-rotate.svg',
          command: () => this.changeInteraction('feature_transform_rotate'),
          active: interactionIdentifier === 'feature_transform_rotate',
          pos: { position: 'bottom' },
        });
        options.push({
          label: 'Move vertex',
          icon: '/icons/toolbox/vertex-move.svg',
          command: () => this.changeInteraction('vertex_transform_translate'),
          active: interactionIdentifier === 'vertex_transform_translate',
          pos: { position: 'bottom' },
        });
        options.push({
          label: 'Add vertex',
          icon: '/icons/toolbox/vertex-add.svg',
          command: () => this.changeInteraction('vertex_draw'),
          active: interactionIdentifier === 'vertex_draw',
          pos: { position: 'bottom' },
        });
        options.push({
          label: 'Delete vertex',
          icon: '/icons/toolbox/vertex-delete.svg',
          command: () => this.changeInteraction('vertex_delete'),
          active: interactionIdentifier === 'vertex_delete',
        });
      }

      options.push({
        label: 'Clone',
        icon: '/icons/toolbox/clone-geometry.svg',
        command: () => this.changeInteraction('clone'),
        active: interactionIdentifier === 'clone',
        pos: { position: 'bottom' },
      });
      options.push({
        label: 'Buffer',
        icon: '/icons/toolbox/line-buffer.svg',
        command: () => this.changeInteraction('buffer'),
        active: interactionIdentifier === 'buffer',
        pos: { position: 'bottom' },
      });
      switch (layerGeometry.geometry.geometry.type) {
        case 'Polygon':
        case 'MultiPolygon':
          if (layerGeometry.geometry.customProperties?.meta_type != 'circle') {
            options.push({
              label: 'Split',
              icon: '/icons/toolbox/polygon-split.svg',
              command: () => this.changeInteraction('split_polygon'),
              active: interactionIdentifier === 'split_polygon',
              pos: { position: 'bottom' },
            });
          }
          break;
          break;
        case 'MultiLineString':
        case 'LineString':
          options.push({
            label: "Split",
            icon: "/icons/toolbox/line-split.svg",
            command: () => this.changeInteraction("split_linestring"),
            active: interactionIdentifier === "split_linestring",
            pos: {position: 'bottom'}
          });
          options.push({
            label: 'Offset',
            icon: "/icons/toolbox/line-offset.svg",
            command: () => this.changeInteraction("offset_line"),
            active: interactionIdentifier === "offset_line",
            pos: {position: 'bottom'}
          })
          break;
        case 'MultiPoint':
          if (layerGeometry?.geometry?.geometry?.coordinates?.length > 1)
            options.push({
              label: "Split",
              icon: "/icons/toolbox/point-split.svg",
              command: () => {
                this.splitPoints(layerGeometry)
              },
              active: interactionIdentifier === "split_point",
              pos: {position: 'bottom'}
            });
          break;
      }


    } else {
      options.push(
        {
          label: "Move point",
          icon: "/icons/toolbox/moveVertex.png",
          command: () => this.changeInteraction("point_transform_translate"),
          active: interactionIdentifier === "point_transform_translate",
          pos: {position: 'bottom'}
        }
      );
      options.push({
        label: 'Clone',
        icon: '/icons/toolbox/clone-geometry.svg',
        command: () => this.changeInteraction('clone'),
        active: interactionIdentifier === 'clone',
        pos: { position: 'bottom' },
      });
    }

    return options;
  }

  splitPoints(layerGeometry: LayerGeometry) {
    console.log(1);
    this.controller.configuration.dialogProvider().open(
      MapMultiSave,
      {
        props: {
          header: this.controller.configuration.translatorProvider("Save split geometries"),
          style: {
            width: isOffline() ? "90vw" : "50vw",
          },
          modal: true
        },
        data: {
          features: layerGeometry?.geometry?.geometry?.coordinates?.map(coord => {
            let obj: any = {
              documentId: layerGeometry.documentId,
              geometry: GeometryUtil.point(coord),
              properties: layerGeometry.attributes,
            };
            if (layerGeometry.document.properties?.header) {
              obj.attributes = layerGeometry.document.properties.header;
            }
            return obj;
          }),
          splitData: {
            documentId: layerGeometry.documentId,
            geometryId: (layerGeometry as any).id || layerGeometry.geometryId
          },
          originalGeometry: layerGeometry,
          mapController: this.controller
        },
        onClose: (options) => {
          switch (options.type) {
            case "config-close":
              this.controller.reloadLayer(layerGeometry.document)
              this.changeInteraction("select")
              this.controller.interaction.clearFocus(true);
              break;
          }
        }
      }
    );
  }

  /*getActionOptions(interactionIdentifier) {
      let options: any = [];
      if (["draw_polygon", "draw_point", "draw_line"].includes(interactionIdentifier))
          options.push(
              {
                  label: "Vertex list",
                  icon: "/icons/toolbox/list.svg",
                  command: () => {
                      if (this.controller.interaction.hasManualInputDialog()) {
                          this.controller.interaction.hideManualInputDialog();
                      } else {
                          this.controller.interaction.showManualInputDialog();
                      }
                      this._mapInstance.fire(MapEvent.Tick);
                  },
                  active: this.controller.interaction.hasManualInputDialog()
              }
          );
      return options;
  }*/

  getMultiActions(layerGeometries, drawMode, point: mapboxgl.Point | undefined = undefined) {
    let actions: any[] = [];

    if (layerGeometries.length >= 2) {
      actions.unshift({
        label: "Merge",
        icon: "/icons/toolbox/merge.svg",
        command: () => this._mapInstance.fire("layer-geometry-merge", {features: layerGeometries})
      });
    }

    return actions;
  }


  async onLayerGeometriesCreate(features: Feature[]): Promise<void> {

    let layer = this.getLayer();
    return new Promise(async (resolve, reject) => {

      /*if (blindInteraction) {
          let endpointData;

          let featuresToSave = features.map((el) => {
              return {
                  documentId: layer.documentId,
                  geometry: el.geometry,
                  attributes: el.properties
              }
          });

              endpointData = featuresToSave
          store.dispatch('saveBulkGeometries', {
              data: endpointData
          }).then(response => {
              this.controller.configuration.toastProvider({
                  severity: "success",
                  summary: this.controller.configuration.translatorProvider('Done'),
                  detail: this.controller.configuration.translatorProvider('Successfully saved!'),
                  life: 5000,
              });
              let layerMap = this.controller.getLayerMap();

              response.forEach((layerGeometry: LayerGeometry) => {
                  layerGeometry.geometry = {
                      id: layerGeometry.geometryId,
                      type: 'Feature',
                      geometry: layerGeometry.geometry,
                      properties: {id: layerGeometry.geometryId, ...layerGeometry.attributes},
                  } as Feature;
                  layerGeometry.document = layerMap.get(layerGeometry.documentId);
                  layerGeometry.document.locationCoordinates.bounds = (layerGeometry as any).documentBounds.bounds;
              });

              this.controller.reloadLayer(layer);
              this._mapInstance.fire(
                  MapEvent.LayerGeometryCreated,
                  {
                      layer: layer,
                      layerGeometries: response
                  }
              );
              resolve(response);

          });
      }
      else*/
      if (layer?.properties?.isZone) {
        this.controller.interaction.addTmpGeometries(features);
        EventBus.emit('zoneCreated');
        return;
      }
      if (layer?.properties?.dataSource?.isLocal) {
        if (!layer.features) layer.features = []
        let savedFeatures = features;
        if (Object.keys(layer?.properties?.header || {}).length) {
          savedFeatures = await this.promptSaveModal(layer, features, true)
          if(savedFeatures) layer.features.push(...savedFeatures.map(x => x.geometry))
        }
        else layer.features.push(...savedFeatures)
        layer.features.map((x, index) => {if (!x.id) x.id = index + 1; if (!x.properties) x.properties = {}; if (!x.properties.id) x.properties.id = index + 1; return x})
        this.controller.reloadLayer(layer);
        this._mapInstance.fire(
          MapEvent.LayerGeometryCreated,
          {
            layer: layer,
            layerGeometries: layer.features
          }
        );
        resolve(layer.features.map(feature => {

          let obj: Layer | any = {
            documentId: layer.documentId,
            geometry: feature
          };
          if (layer.properties?.header) {
            obj.attributes = layer.properties.header;
            obj.properties = obj.geometry.properties || {};
          }
          return obj;
        }));
        return;
      }

      this.promptSaveModal(layer, features).then((response: any) => resolve(response));

    });
  }

  promptSaveModal(layer, features, saveAsEmitOnly?): Promise<Feature[]> {
    return new Promise((resolve, reject) => {
      this.controller.configuration.dialogProvider().open(
        MapMultiSave,
        {
          props: {
            header: this.controller?.configuration?.translatorProvider("Save"),
            style: {
              width: isOffline() ? "90vw" : "50vw",
            },
            modal: true,
            closeOnEscape: false,
          },
          data: {
            features: features.map(feature => {

              let obj: Layer | any = {
                documentId: layer.documentId,
                geometry: feature
              };
              if (layer.properties?.header) {
                obj.attributes = layer.properties.header;
                obj.properties = obj.geometry.properties || {};
              }
              return obj;
            }),
            mapController: this.controller,
            saveAsEmitOnly: saveAsEmitOnly
          },
          onClose: (options: any) => {
            switch (options.type) {
              case 'config-close':
                if (options.data === undefined) {
                  resolve(undefined);
                  return;
                }
                this.controller.reloadLayer(layer);
                this._mapInstance.fire(
                  MapEvent.LayerGeometryCreated,
                  {
                    layer: layer,
                    layerGeometries: options.data
                  }
                );
                resolve(options.data)
                break;
            }
          }
        }
      )
    });
  }

  async onLayerGeometriesUpdate(layerGeometry: LayerGeometry, feature: Feature) {
    const layer = this.getLayer();
    if (layer?.properties?.isZone) {
      this.controller.interaction.clearTmpGeometries();
      this.controller.interaction.addTmpGeometries([feature]);
      EventBus.emit('zoneUpdated');
      return;
    }

    if (layer?.properties?.dataSource?.isLocal) {
      if(layerGeometry.document) layerGeometry.document =layer;
      let f = layer.features?.find(x => parseInt(x.id ?? x.geometryId) === parseInt(feature.id));
      if (f) f.geometry = feature.geometry;
      this.controller.reloadLayer(layer);
      this._mapInstance.fire(
        MapEvent.LayerGeometryUpdated,
        {
          layer: layerGeometry.document,
          layerGeometry: layerGeometry,
        }
      )
      this.controller.interaction.clearFocus(true);
      this.controller.interaction.mode.resetInteraction();
      this._mapInstance.fire(MapEvent.Tick);
      return;
    }

    layerGeometry = await this.saveLayerGeometry(layerGeometry, feature, layerGeometry.document);

    this.controller.interaction.addToFocus(layerGeometry);

    this.controller.reloadLayer(layerGeometry.document);

    this._mapInstance.fire(
      MapEvent.LayerGeometryUpdated,
      {
        layer: layerGeometry.document,
        layerGeometry: layerGeometry,
      }
    )
  }

  onInit() {
    super.onInit();
    this.setupLayerGeometryRemoveHandler();
  }

  async saveLayerGeometry(layerGeometry: LayerGeometry, feature: Feature, activeModeLayer: Layer | undefined) {
    layerGeometry.geometry.geometry = JSON.parse(JSON.stringify(feature.geometry));
    layerGeometry.attributes = JSON.parse(JSON.stringify(feature.properties));
    let result: LayerGeometry = await store.dispatch("updateGeometry", {
      query: {
        asFeature: true,
      },
      data: {
        geometryId: layerGeometry.geometryId,
        documentId: activeModeLayer?.documentId || layerGeometry.documentId || parseInt(layerGeometry?.layer?.source?.replace("syncmap_source_", "")),
        geometry: feature,
        attributes: layerGeometry.attributes ?? {},
      },
      headers: {
        'x-access-token': this.controller.configuration.accessToken
      }
    }).catch((error) => {
      this.controller.configuration.toastProvider({
        severity: "error",
        summary: "Error",
        detail: this.controller.configuration.translatorProvider(error),
        life: 5000,
      });
    });
    result.document = activeModeLayer;
    if (result.document) {
      if (!result.document.locationCoordinates) result.document.locationCoordinates = {}
      result.document.locationCoordinates.bounds = (result as any)?.documentBounds?.bounds;
    }
    return result;
  }


  public setupLayerGeometryRemoveHandler(): void {
    this._mapInstance.on(
      MapEvent.LayerGeometryRemove,
      (event: {features: LayerGeometry[]}) => {
        let layer = this.getLayer();
        if (layer?.properties?.dataSource?.isLocal) {
          event.features?.forEach(feature => {
            let ff = layer.features?.findIndex(x => parseInt(x.id) === parseInt(feature.id));
            if (ff > -1) layer.features.splice(ff, 1)
          })

          this.controller.reloadLayer(layer);
          this.controller.interaction.clearFocus(true);
          this.controller.interaction.mode.resetInteraction();
          this.controller.mapInstance.fire(MapEvent.LayerGeometryRemoved, {layer: layer});
          this._mapInstance.fire(MapEvent.Tick);
        }
        else
          this.controller.configuration.confirmProvider().require({
            header: this.controller.configuration.translatorProvider("Delete?"),
            message: this.controller.configuration.translatorProvider("Are you sure want to delete the selected features?"),
            icon: "pi pi-exclamation-triangle",
            accept: () => {
              store
                .dispatch("deleteGeometry", {
                  query: {
                    moduleId: this.controller.configuration.moduleId,
                  },
                  data: {
                    geometryIds: event.features.filter(layerGeometry => layerGeometry?.document?.documentId).map(layerGeometry => layerGeometry.geometryId),
                  },
                })
                .then((response) => {
                  this.controller.interaction.clearFocus(true);
                  this.controller.reloadLayer(this.getLayer());
                  this.controller.mapInstance.fire(MapEvent.LayerGeometryRemoved, {layer: this.getLayer()});
                })
                .catch((error) => {
                  console.error(error);
                });
            },
            reject: () => {

            },
          });
      }
    );
  }
}
